import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
} from 'firebase/firestore';
import { LINE_STATUSES, ORDER_STATUSES } from '../firebase/schema';
import { reconcileStuckReadyToOrderOrders } from './orderingMarkOrdered';
import { filterByFacility } from './facilities';

function buildReadyToOrderPayload(ordersList, allReadyLines, facilityId) {
  const orderFacilityById = new Map(ordersList.map((o) => [o.id, o.facilityId]));
  const enrichedLines = allReadyLines.map((line) => ({
    ...line,
    facilityId: line.facilityId || orderFacilityById.get(line.orderId),
  }));

  const filteredOrders = filterByFacility(ordersList, facilityId);
  const filteredLines = filterByFacility(enrichedLines, facilityId);

  const linesByOrderTemp = {};
  filteredLines.forEach((line) => {
    if (!linesByOrderTemp[line.orderId]) linesByOrderTemp[line.orderId] = [];
    linesByOrderTemp[line.orderId].push(line);
  });

  const visibleOrders = filteredOrders.filter(
    (order) => (linesByOrderTemp[order.id] || []).length > 0
  );

  return {
    ordersList: filteredOrders,
    linesByOrderTemp,
    visibleOrders,
  };
}

/**
 * Subscribe to orders/lines in the Ready To Order queue.
 * Uses two listeners (orders + lines) instead of per-order line queries.
 * Filters by active facilityId (missing facilityId on docs ⇒ central).
 */
export function subscribeReadyToOrderData(db, { onData, onError, facilityId }) {
  let ordersList = [];
  let allReadyLines = [];
  let ordersReady = false;
  let linesReady = false;

  const emit = async () => {
    if (!ordersReady || !linesReady) return;

    const { ordersList: scopedOrders, linesByOrderTemp, visibleOrders } =
      buildReadyToOrderPayload(ordersList, allReadyLines, facilityId);

    await reconcileStuckReadyToOrderOrders(db, scopedOrders, linesByOrderTemp);

    onData({
      orders: visibleOrders,
      linesByOrder: Object.fromEntries(
        visibleOrders.map((order) => [order.id, linesByOrderTemp[order.id] || []])
      ),
    });
  };

  const ordersUnsub = onSnapshot(
    query(collection(db, 'orders'), where('status', '==', ORDER_STATUSES.READY_TO_ORDER)),
    (snapshot) => {
      ordersList = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      ordersReady = true;
      emit().catch((err) => onError?.(err));
    },
    (err) => onError?.(err)
  );

  const linesUnsub = onSnapshot(
    query(collection(db, 'orderLines'), where('status', '==', LINE_STATUSES.READY_TO_ORDER)),
    (snapshot) => {
      allReadyLines = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      linesReady = true;
      emit().catch((err) => onError?.(err));
    },
    (err) => onError?.(err)
  );

  return () => {
    ordersUnsub();
    linesUnsub();
  };
}

/** One-time fetch (e.g. after mark-as-ordered) — same shape as subscribe callback. */
export async function fetchReadyToOrderData(db, facilityId) {
  const [ordersSnapshot, linesSnapshot] = await Promise.all([
    getDocs(query(collection(db, 'orders'), where('status', '==', ORDER_STATUSES.READY_TO_ORDER))),
    getDocs(
      query(collection(db, 'orderLines'), where('status', '==', LINE_STATUSES.READY_TO_ORDER))
    ),
  ]);

  const ordersList = ordersSnapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));

  const allReadyLines = linesSnapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));

  const { ordersList: scopedOrders, linesByOrderTemp, visibleOrders } =
    buildReadyToOrderPayload(ordersList, allReadyLines, facilityId);

  await reconcileStuckReadyToOrderOrders(db, scopedOrders, linesByOrderTemp);

  return {
    orders: visibleOrders,
    linesByOrder: Object.fromEntries(
      visibleOrders.map((order) => [order.id, linesByOrderTemp[order.id] || []])
    ),
  };
}
